import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useSession } from "@/features/auth/use-session";
import { AppProviders } from "@/providers/AppProviders";
import { AppLayout } from "@/layouts/AppLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import SignInPage from "@/pages/SignIn";
import SignUpPage from "@/pages/SignUp";
import VerifyEmailPage from "@/pages/VerifyEmail";
import CollectionsPage from "@/pages/Collections";
import CollectionPage from "@/pages/Collection";

function Authenticated({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  if (isPending) {
    return (
      <div className="grid min-h-svh place-items-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!session?.user) return <Navigate to="/sign-in" replace />;
  return <AppProviders>{children}</AppProviders>;
}

function Anonymous({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  if (isPending) return null;
  if (session?.user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth — anonymous */}
        <Route
          element={
            <Anonymous>
              <AuthLayout />
            </Anonymous>
          }
        >
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
        </Route>
        <Route path="/verify-email" element={
          <AuthLayout />
        }>
          <Route index element={<VerifyEmailPage />} />
        </Route>

        {/* App — gated */}
        <Route
          element={
            <Authenticated>
              <AppLayout />
            </Authenticated>
          }
        >
          <Route path="/" element={<Navigate to="/collections" replace />} />
          <Route path="/collections" element={<CollectionsPage />} />
          <Route path="/collections/:id" element={<CollectionPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
